import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import {
  CanvasElement,
  CanvasElementFrom,
  CanvasElementId,
  CanvasElementType,
} from '@blaze-runtime/engine-domain';
import { countedShareReplay } from '@blaze-shared/shared-util';

import {
  isDetailsView,
  isEditableForm,
  isElementType,
  isFormDetailsList,
  isFormList,
  isWizard,
} from '../canvas-elements/element-model/element-type-guards';
import { findParentByElementType, findParentFormGroupLike } from '../utils/layout/relationship';

import { ElementStoreViewService } from './element-store-view.service';

@Injectable({ providedIn: 'root' })
export class ElementHierarchyService {
  private readonly elementStoreView = inject(ElementStoreViewService);

  getParentElement(
    elementId: CanvasElementId | undefined,
    elementType: CanvasElementType,
    checkCurrent = false,
  ): Observable<CanvasElement | undefined> {
    if (!elementId) {
      return of(undefined);
    }

    return this.elementStoreView.entities$.pipe(
      map((entities) => findParentByElementType(entities, elementId, elementType, checkCurrent)),
      countedShareReplay(),
    );
  }

  findParentWizard(
    elementId: string | undefined,
  ): Observable<CanvasElementFrom<'wizard'> | undefined> {
    if (!elementId) {
      return of(undefined);
    }
    return this.elementStoreView.entities$.pipe(
      map((elements) => {
        const element = elements[elementId];
        if (
          (isEditableForm(element) && element.props.content.origin === 'wizard') ||
          (isFormList(element) && element.props.content.origin === 'wizard') ||
          (isDetailsView(element) && element.props.content.origin === 'wizard') ||
          (isElementType(element, 'table') && element.props.content.table?.origin === 'wizard') ||
          (isFormDetailsList(element) && element.props.content.origin === 'wizard')
        ) {
          const wizard = findParentFormGroupLike(elements, elementId);
          if (!isWizard(wizard)) {
            throw new Error('parent wizard not found');
          }
          return wizard;
        }
        return undefined;
      }),
    );
  }
}
